import { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import API from "../../../service/api";
import { MDBBtn, MDBIcon, MDBInput } from "mdb-react-ui-kit";
import { TabsViewer } from "../../../utils/TabsViewer";
import { DeleteConfirmModal } from "../../../components/modals/DeleteConfirmModal";
import { AddLocationModal } from "./AddLocationModal";
import { DataTable } from "../../../components/DataTable";
import { LocationsGIS } from "../../../components/LocationsGIS";
import { AddUserModal } from "./AddUserModal";
import { CustomSelect } from "../../../components/CustomSelect";
import { ShareGroupModal } from "./ShareGroupModal";
import { TextInput } from "../../../components/core/inputs/TextInput";

export interface GroupEditorProps {
    groupId: number;
    canEdit: boolean;
    canDelete: boolean;
    onClose: () => void;
    onLocationAdd: () => void;
    onLocationRemove: () => void;
    onUserAdd: () => void;
    onUserRemove: () => void;
    onGroupDelete: () => void;
    onGroupEdit: () => void;

}

export const GroupEditor = ({groupId, canEdit, canDelete, onClose, onLocationAdd, onLocationRemove, onUserAdd, onUserRemove, onGroupDelete, onGroupEdit}: GroupEditorProps) => {
    const [group, setGroup] = useState<any|null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [changed, setChanged] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [editGroupName, setEditGroupName] = useState('');
    const { t } = useTranslation();

    const canEditGroup = useMemo<boolean>(() => group?.Change, [canEdit, group]);
    const canDeleteGroup = useMemo<boolean>(() => group?.Delete, [group]);
    const canShareGroup = useMemo<boolean>(() => group?.Share, [group]);
    const canViewLocations = useMemo<boolean>(() => group?.locations, [group]);
    const canViewUsers = useMemo<boolean>(() => group?.users, [group]);
    const canAssignUser = useMemo<boolean>(() => group?.UserAssociation, [group]);
    const canAssignLocations = useMemo<boolean>(() => group?.AssignLocations, [group]);

    const fetch = useCallback(() => {
        setLoading(true);
        setError('');
        setEditMode(false);
        setEditGroupName('');
        setChanged(false);
        API.GET('/api/admgroups/'+groupId)
            .then(setGroup)
            .catch(err => {
                setError(err.message);
                setGroup(null);
            })
            .finally(() => { setLoading(false); })
    }, [groupId]);

    useEffect(() => {
        fetch();
    }, [groupId]);

    if (!group) return null;

    return (
        <div className="d-flex flex-column overflow-hidden card">
            <button type="button" className="btn-close position-absolute" style={{right: 5, top: 5}} onClick={() => onClose ? onClose() : {}}></button>
            <div className="d-flex flex-column card-body overflow-hidden">
                <div className="d-flex flex-column w-100 justify-content-center text-start">
                    <MDBIcon icon="fas fa-layer-group" size="4x" className="mb-2"/>
                    <div className="d-flex flex-row  justify-content-center">
                        {!editMode && <h5 className="text-center mb-1" style={{marginLeft: 40, marginRight: 40}}>{group.Name || ''}</h5> }
                        { editMode && (
                            <div className="d-flex flex-row justify-content-center align-items-center w-100">
                                <TextInput label={t('name')} value={editGroupName} onValueChange={name => { setEditGroupName(name); setChanged(true); }}/>
                                <span className={"position-absolute " + (changed ? 'text-success' : 'text-muted')} style={{marginLeft: 400+30, opacity: changed ? 1 : 0.25, cursor: changed ? 'pointer' : 'default'}} role="button" 
                                    onClick={() => {
                                        setChanged(false);
                                        API.PATCH(`/api/admgroups/${groupId}`, {Name: editGroupName})
                                            .then(() => {
                                                fetch();
                                                onGroupEdit();
                                            })
                                            .finally(() => {
                                                setEditMode(false);
                                            });
                                    }}><MDBIcon icon="fas fa-check" size="lg"/></span>
                                <span className="text-danger position-absolute" style={{marginLeft: 400+75}} role="button" onClick={() => {
                                    setEditMode(false);
                                }}><MDBIcon icon="fas fa-xmark" size="lg"/></span>
                            </div>
                            
                        )}
                        {(canEditGroup && !editMode && !error) && <span role="button" className="position-absolute" style={{right: 35}} onClick={() => { setEditGroupName(group.Name); setEditMode(true); }}><MDBIcon icon="fas fa-pen-to-square" fixed/></span> }
                        {(canDeleteGroup && !editMode && !error) && <span role="button" className="position-absolute" style={{right: 15}} onClick={() => { onGroupDelete(); }}><MDBIcon icon="fas fa-trash-can" color="danger" fixed/></span>}
                        
                    </div>
                    { group.shared && <h6 className="text-muted">Shared by {group.groupCompany?.name}</h6> }
                    { group?.gType == 1 && <div className="alert alert-warning alert-dismissible fade show mt-2" role="alert">
                        <MDBIcon icon="fas fa-bookmark" color="warning" className="me-1"/>{t('special_ungrouped_group_prompt')}
                    </div> }
                    
                </div>

                {loading && (
                    <div className="d-flex w-100 justify-content-center">
                        <div className="spinner-border" role="status">
                            <span className="visually-hidden">{t('loading...')}</span>
                        </div>
                    </div>
                    
                )}

                {(error && !loading) && (
                    <div className="alert alert-danger alert-dismissible fade show mt-2" role="alert">
                        { error }
                    </div>
                )}
                {(!error && !loading && (canViewLocations || canViewUsers || canShareGroup)) && (<div className="w-100 d-flex flex-column align-items-center overflow-hidden">
                        <TabsViewer
                            id="admgroups" fullSize
                            defaultTab=""
                            tabs={[
                                ...(canViewLocations ? [{
                                    name: 'locations', title: t('locations'), icon: 'fas fa-map-marker-alt',
                                    component: <GroupEditorLocationsTab groupId={groupId} canAssignLocations={canAssignLocations} locations={group.locations} onLocationAdd={() => { onLocationAdd(); fetch(); }} onLocationRemove={() => { onLocationRemove(); fetch(); }}/>
                                }] : []),
                                ...(canViewUsers ? [{
                                    name: 'users', title: t('users'), icon: 'fas fa-user',
                                    component: <GroupEditorUsersTab users={group.users} canAssignUser={canAssignUser} groupId={groupId} onEdit={() => fetch()} onUserAdd={() => {onUserAdd(); fetch(); }} onUserRemove={() => {onUserRemove(); fetch();}}/>
                                }] : []),
                                ...(canShareGroup ? [{
                                    name: 'shared', title: t('shared'), icon: 'fas fa-retweet',
                                    component: <GroupEditorSharedTab groupId={groupId} companies={group.sharedTo} canShare={canShareGroup} onDelete={() => { fetch(); onGroupEdit(); }} onShare={() => {fetch(); onGroupEdit(); }}/>
                                }] : [])
                            ]}
                        />
                </div>)}
            </div>
        </div>
    )
}

const GroupEditorLocationsTab = ({groupId, canAssignLocations, locations, onLocationAdd, onLocationRemove}) => {
    const [deleteLocationId, setDeleteLocationId] = useState<number|null>(null);
    const [selectedLocation, setSelectedLocation] = useState<any|null>(null);
    const [locationError, setLocationError] = useState('');
    const [openAddLocation, setOpenAddLocation] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        setDeleteLocationId(null);
        setSelectedLocation(null);
        setLocationError('');
    }, [groupId]);

    const fetch = useCallback((id: number) => {
        API.GET(`/api/admlocations/${id}`)
                .then(l => setSelectedLocation(l))
                .catch(err => { setLocationError(err.message); setSelectedLocation(null);});
    }, []);

    return (
        <div className="w-100 d-flex flex-column overflow-auto mt-1">
            <DeleteConfirmModal
                open={deleteLocationId !== null}
                title={t('remove_location')}
                message={t('remove_location_from_group_prompt')}
                onCancel={() => setDeleteLocationId(null)}
                onConfirm={() => {
                    API.DELETE(`/api/admgroups/${groupId}/l/${deleteLocationId}`)
                        .then(() => {
                            if (deleteLocationId == selectedLocation?.id) setSelectedLocation(null);
                            onLocationRemove();
                        })
                        .catch(err => {
                            alert(err.message);
                        })
                    setDeleteLocationId(null);
                }}
            />

            <AddLocationModal
                groupId={groupId}
                open={openAddLocation}
                onClose={() => setOpenAddLocation(false)}
                onConfirm={id => {
                    API.POST(`/api/admgroups/${groupId}/l/${id}`)
                        .then(() => {
                            onLocationAdd();
                        })
                        .catch(err => {
                            alert(err.message);
                        })
                    setOpenAddLocation(false);
                }}
            />
            {!selectedLocation &&
                <div className="d-flex flex-column w-100 mt-1">
                    { canAssignLocations && <div className="d-flex w-100 justify-content-end">
                        <MDBBtn color="link" onClick={() => setOpenAddLocation(true)}>
                            <MDBIcon icon="plus-circle" className="me-1"/>
                            {t('add_location')}
                        </MDBBtn>
                    </div> }
                    <DataTable<any>
                        height={250}
                        data={locations}
                        onRowSelected={l => fetch(l.id)}
                        columns={[
                            {headerName: t('name'), valueGetter: ({data}) => ({Name: data.Name, lType: data.lType, address: data.address}), flex: 3, cellRenderer: ({value}) => (
                                <span className="d-flex flex-row align-items-center w-100 h-100">
                                    <MDBIcon icon="map-marker-alt" color="primary" className="me-1"/>
                                    {value.lType == 1 && <MDBIcon icon="bookmark" color="warning" className="me-1"/>}
                                    {value.lType == 2 && <MDBIcon icon="gear" color="info" className="me-1"/>}
                                    <div className="d-flex flex-column" style={{lineHeight: value.address?'0.8rem':'1rem'}}>
                                        <span className={value.lType == 1?"fst-italic":""}>{value.Name}</span>
                                        <span className="text-muted" style={{fontSize: '80%'}}>{value.address}</span>
                                    </div>
                                </span>
                            )},
                            {headerName: t('machines'), field: 'nrOfMachines', flex: 1}
                        ]}
                        rowClassname={l => l.lType == 1 ? 'list-group-item-warning fst-italic warning-row' : ''}
                    />
                </div>
            }

            {selectedLocation && (
                <div className="d-flex flex-column mt-1 pt-2 overflow-auto px-4" style={{borderTop: '2px solid #d7d7d7'}}>
                    
                    <div className="d-flex flex-row align-items-center mb-1">
                        <div className="d-flex flex-row justify-content-start">
                            <span className="me-2" style={{width: 30}} onClick={() => setSelectedLocation(null)}><MDBIcon icon="fas fa-arrow-left-long" size="lg"/></span>
                            <MDBIcon icon="fas fa-map-marker-alt" size="xl" color="primary"/><h5 className="m-0 ms-1">{selectedLocation?.Name || '?'}</h5>
                        </div>
                        <div className="d-flex flex-row justify-content-end flex-fill">
                            { canAssignLocations && <button type="button" className="btn btn-danger px-3 py-2" data-mdb-ripple-color="dark" onClick={() => setDeleteLocationId(selectedLocation.id)}>{t('delete')}</button> }
                        </div>
                    </div>

                    {locationError && (
                        <div className="alert alert-danger alert-dismissible fade show mt-2" role="alert">
                            { locationError }
                        </div>
                    )}

                    {(!locationError && selectedLocation) && (
                        <>
                            <div className="d-flex flex-column">
                                <div className="d-flex flex-column" style={{height: 200}}>
                                    <LocationsGIS locations={[selectedLocation]} center={selectedLocation.geo} zoom={12} noPopups />
                                </div>
                                <div className="d-flex flex-column mt-1 px-2 flex-fill">
                                    {selectedLocation.address && <>
                                        <h6 className="mb-0">{t('address')}</h6>
                                        <span className="mb-1">{selectedLocation.address}</span>
                                    </>}
                                    {selectedLocation.Info && <>
                                        <h6 className="mb-0">{t('info')}</h6>
                                        <span className="mb-1">{selectedLocation.Info}</span>
                                    </>}
                                    
                                    <h6 className="mb-0">{t('groups')}</h6>
                                    <div className="d-flex flex-column mb-1" style={{maxHeight: 120, overflow: 'auto'}}>
                                        { ( !selectedLocation.groups || selectedLocation.groups.length == 0) && <span className="text-muted">{t('none_lower')}</span> }
                                        { selectedLocation.groups?.map(g => (
                                            <div key={g.id} className="d-flex flex-row align-items-center ms-1"><MDBIcon icon="fas fa-layer-group" color="primary" className="me-1"/>{g.Name}</div>
                                        )) }
                                    </div>
                                    <h6 className="mb-0">{t('machines')}</h6>
                                    <div className="d-flex flex-column mb-1" style={{maxHeight: 120, overflow: 'auto'}}>
                                        { (!selectedLocation.machines || selectedLocation.machines.length == 0) && <span className="text-muted">{t('none_lower')}</span> }
                                        { selectedLocation.machines?.map(g => (
                                            <div key={g.id} className="d-flex flex-row align-items-center ms-1"><MDBIcon icon="fas fa-server" color="primary" className="me-1"/>{g.name}</div>
                                        )) }
                                    </div>
                                </div>
                            </div>
                        </>
                    )}
                </div>
            )}
        </div>
    )

}

const GroupEditorUsersTab = ({groupId, canAssignUser, users, onEdit, onUserAdd, onUserRemove}) => {
    const [changed, setChanged] = useState(false);
    const [permissions, setPermissions] = useState<any[]>([]);
    const [selectedId, setSelectedId] = useState<number|null>(null);
    const [deleteUserId, setDeleteUserId] = useState<number|null>(null);
    const [selectedUser, setSelectedUser] = useState<any|null>(null);
    const [selectedUserRightsId, setSelectedUserRightsId] = useState(-1);
    const [userError, setUserError] = useState('');
    const [openAddUser, setOpenAddUser] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        setSelectedId(null);
    }, [groupId]);

    useEffect(() => {
        setUserError('');

        if (selectedId && selectedId > 0) {
            API.GET(`/api/admgroups/${groupId}/u/${selectedId}`)
                .then(l => {
                    API.GET('/api/rights')
                        .then(r => {
                            setPermissions(r.rights);
                            setSelectedUserRightsId(l.groupRights?.id);
                            setSelectedUser(l)
                        })
                        .catch(err => { setUserError(err.message); setSelectedUser(null);});
                               
                })
                .catch(err => { setUserError(err.message); setSelectedUser(null);});
        }else {
            setSelectedUser(null);
        }
    }, [selectedId]);

    const Table = useMemo(() => (
        <DataTable<any>
            data={users}
            height={250}
            columns={[
                {headerName: t('name'), valueGetter: ({data}) => ({Name: data.Name, pending: data.pending}), flex: 1, cellRenderer: ({value}) => (
                    <span>{value.pending && <MDBIcon icon="fas fa-hourglass" color="primary" className="me-1"/>}{value.Name||t('pending_invitation')}</span>
                )},
                {headerName: t('email'), field: 'mail', flex: 1},
                {headerName: t('permissions'), valueGetter: ({data}) => data.groupRights?.Name??'', flex: 1}
            ]}
            onRowSelected={l => setSelectedId(l.id)}
            rowClassname={u => u.pending ? 'fst-italic list-group-item-warning warning-row':''}
        />
    ), [users, selectedId]);


    return (
        <div className="w-100 d-flex flex-column overflow-hidden mt-1">
            <DeleteConfirmModal
                open={deleteUserId !== null}
                title={t('remove_user')}
                message={t('remove_user_from_group_prompt')}
                onCancel={() => setDeleteUserId(null)}
                onConfirm={() => {
                    API.DELETE(`/api/admgroups/${groupId}/u/${deleteUserId}`)
                        .then(() => {
                            onUserRemove();
                            if (selectedId == deleteUserId) setSelectedId(null);
                        });
                    setDeleteUserId(null);
                }}
            />
            <AddUserModal
                groupId={groupId}
                open={openAddUser}
                onClose={() => setOpenAddUser(false)}
                onConfirm={(userId, permissionId) => {
                    API.POST(`/api/admgroups/${groupId}/u/${userId}`, {rightsId: permissionId})
                        .then(() => {
                           onUserAdd();
                        });
                    setOpenAddUser(false);   
                }}
            />

            {!selectedId &&
                <div className="d-flex flex-column w-100">
                    { canAssignUser && <div className="d-flex flex-row w-100 justify-content-end">
                        <MDBBtn color="link" onClick={() => setOpenAddUser(true)}>
                            <MDBIcon icon="plus-circle" className="me-1"/>
                            {t('add_user_to_group')}
                        </MDBBtn>
                    </div> }
                    {Table}
                </div>
            }
            
            {(selectedId&&selectedUser) && (
                <div className="d-flex flex-column mt-4 mx-4 pt-2" style={{borderTop: '2px solid #d7d7d7'}}>
                    
                    <div className="d-flex flex-row align-items-center mb-1">
                        <div className="d-flex flex-row justify-content-start">
                            <span className="me-2" style={{width: 30}} onClick={() => setSelectedId(null)}><MDBIcon icon="fas fa-arrow-left-long" size="lg"/></span>
                            <MDBIcon icon="fas fa-user" size="xl" color="primary"/>
                            {!selectedUser.pending && <h5 className="m-0 ms-1">{selectedUser?.Name || '?'}</h5>}
                            {selectedUser.pending && <h5 className="m-0 ms-1 fst-italic">{t('pending_invitation')}</h5>}
                        </div>
                        <div className="d-flex flex-row justify-content-end flex-fill">
                            {canAssignUser && <button type="button" className="btn btn-danger px-3 py-2" data-mdb-ripple-color="dark" onClick={() => setDeleteUserId(selectedId)}>{t('delete')}</button>}
                        </div>
                        
                    </div>

                    {userError && (
                        <div className="alert alert-danger alert-dismissible fade show mt-2" role="alert">
                            { userError }
                        </div>
                    )}

                    {(!userError && selectedUser) && (
                        <>
                            <div className="d-flex flex-column">
                                {selectedUser.pending && <>
                                    <ul className="list-group list-group-light p-0">
                                        <li className="list-group-item p-1"><MDBIcon icon="fas fa-envelope" fixed color="primary" className="me-1"/><span className="fw-bold me-1">{t('email')}:</span>{selectedUser.mail}</li>
                                    </ul>
                                </>}
                                {!selectedUser.pending && <>
                                    <ul className="list-group list-group-light p-0">
                                        <li className="list-group-item p-1"><MDBIcon icon="fas fa-id-card" color="primary" fixed className="me-1"/><span className="fw-bold me-1">{t('name')}:</span>{selectedUser.Name}</li>
                                        <li className="list-group-item p-1"><MDBIcon icon="fas fa-envelope" color="primary" fixed className="me-1"/><span className="fw-bold me-1">{t('email')}:</span>{selectedUser.mail}</li>
                                        <li className="list-group-item p-1"><MDBIcon icon="fas fa-mobile-alt" color="primary" fixed className="me-1"/><span className="fw-bold me-1">{t('phone')}:</span>{selectedUser.phone}</li>
                                    </ul>
                                </>}
                                
                                {(canAssignUser && permissions.length > 0) && (
                                    <div className="d-flex mb-3 flex-row align-items-center w-100">
                                        <MDBIcon icon="fas fa-briefcase" color="primary" fixed className="me-2"/>
                                        <CustomSelect w100 title="Group permissions" items={permissions.map(p => ({ val: p.id, title: p.Name }))} selected={selectedUserRightsId} onSelect={id => {setSelectedUserRightsId(id as number); setChanged(true); }} />
                                        <button className="btn btn-primary ms-1 py-2" disabled={!changed} onClick={() => {
                                            setChanged(false);
                                            API.PATCH(`/api/admgroups/${groupId}/u/${selectedUser.id}`, {rightsId: selectedUserRightsId})
                                                .then(() => onEdit())
                                                .catch(err => {

                                                });
                                        }}>{t('save')}</button>
                                    </div>
                                )}
                                {!canAssignUser && (
                                    <ul className="list-group list-group-light p-0"><li className="list-group-item p-1"><MDBIcon icon="fas fa-briefcase" color="primary" fixed className="me-1"/><span className="fw-bold me-1">{t('permissions')}:</span>{selectedUser.userRights?.Name||''}</li></ul>
                                )}
                            </div>
                            
                        </>
                    )}
                </div>
            )}

            {(!selectedUser && userError) && (
                <div className="alert alert-danger alert-dismissible fade show mt-2" role="alert">
                    { userError }
                </div>
            )}
        </div>
    )

}

const GroupEditorSharedTab = ({groupId, companies, canShare, onDelete, onShare}) => {
    const [deleteId, setDeleteId] = useState(null);
    const [openShareGroup, setOpenShareGroup] = useState(false);
    const { t } = useTranslation();

    return (
        <div className="w-100 d-flex flex-column overflow-hidden mt-1">
            <DeleteConfirmModal
                open={deleteId !== null}
                title={t('unshare_group')}
                message={t('unshare_group_prompt')}
                onCancel={() => setDeleteId(null)}
                onConfirm={() => {
                    API.DELETE(`/api/admgroups/${groupId}/share/${deleteId}`).then(() => {
                        onDelete();
                    });
                    setDeleteId(null);
                }}
            />

            <ShareGroupModal
                groupId={groupId}
                open={openShareGroup}
                onClose={() => setOpenShareGroup(false)}
                onConfirm={() => onShare()}
            />
            <div className="d-flex flex-column w-100">
                { canShare &&
                    <div className="d-flex flex-row w-100 justify-content-end">
                        <MDBBtn color="link" onClick={() => setOpenShareGroup(true)}>
                            <MDBIcon icon="share" className="me-1"/>
                            {t('share')}
                        </MDBBtn>
                    </div>
                }
                <DataTable<any>
                    data={companies??[]}
                    height={250}
                    columns={[
                        {headerName: t('name'), field: 'name', flex: 1},
                        {headerName: t('cif'), field: 'CIF', flex: 1},
                        {headerName: t('permissions'), valueGetter: ({data}) => data.rights?.Name??'', flex: 1},
                        ...(canShare ? [
                            {headerName: t('unshare'), field: 'id', flex: 1, cellRenderer: ({value}) => (
                                <span><span role="button" onClick={() => setDeleteId(value)}><MDBIcon icon="fas fa-trash-can" color="danger"/></span></span>
                            )}
                        ] : [])
                    ]}    
                />
            </div>
            
        </div>
    )
}